
import { InfoBox, WarningBox, TipBox, StepList, Step } from './ContentComponents';
import type { ContentBlock } from '@/lib/articleContent';
import { sanitizeCmsHtml, looksLikeHtml } from '@/lib/cms-html';
import { smileyStripUrl } from '@/lib/xat-smilie';
import { cn } from '@/lib/utils';
import PowerMentionText, {
  PowerMentionHtml,
  PowerMentionParagraph,
} from '@/components/help-center/powers/PowerMentionText';

/** Código xat tipo (smile), (wink), … */
const SMILIE_PARENS_CODE = /^\([^)]+\)$/;

function smilieInnerFromParensCell(codeCell: string): string | null {
  const t = codeCell.trim();
  if (!SMILIE_PARENS_CODE.test(t)) return null;
  return /^\(([^)]+)\)$/.exec(t)?.[1]?.trim() ?? null;
}

function prepareSmilieShortcutsTable(block: Extract<ContentBlock, { type: 'table' }>) {
  const headers = block.headers.map((h) => h.trim());
  const viraIdx = headers.findIndex((h) => h.toLowerCase() === 'vira');
  let resultIdx = headers.findIndex((h) => h.toUpperCase() === 'RESULTADO');

  const isTwoColShortcutTable =
    headers.length === 2 &&
    headers[0].toLowerCase() === 'digite' &&
    viraIdx === 1 &&
    resultIdx < 0;

  const displayHeaders = isTwoColShortcutTable ? [...headers, 'RESULTADO'] : headers;
  const displayRows = isTwoColShortcutTable
    ? block.rows.map((r) => [...r, ''])
    : block.rows;

  if (isTwoColShortcutTable) {
    resultIdx = displayHeaders.length - 1;
  }

  return { displayHeaders, displayRows, viraIdx, resultIdx };
}

export function ArticleHtmlChunk({
  html,
  className,
  asPlainParagraph = true,
}: {
  html: string;
  className?: string;
  asPlainParagraph?: boolean;
}) {
  const t = html.trim();
  if (!t) return null;
  if (looksLikeHtml(t)) {
    return (
      <PowerMentionHtml
        html={sanitizeCmsHtml(t)}
        className={cn(
          'prose prose-lg max-w-none text-left text-gray-700 [&_img]:rounded-lg [&_pre]:rounded-xl [&_pre]:bg-[#0d1117] [&_pre]:p-4 [&_pre]:text-emerald-100 [&_a]:text-blue-600',
          className
        )}
      />
    );
  }
  if (asPlainParagraph) {
    return <PowerMentionParagraph html={html} className={className} />;
  }
  return <PowerMentionText text={html} className={className} as="span" />;
}

export function renderArticleBlock(block: ContentBlock, idx: number) {
  switch (block.type) {
    case 'heading':
      return block.level === 2 ? (
        <h2 key={idx} className="mb-4 mt-10 font-poppins text-2xl font-bold text-gray-900 first:mt-0">
          {block.text}
        </h2>
      ) : (
        <h3 key={idx} className="mb-3 mt-7 font-poppins text-lg font-bold text-gray-900">
          {block.text}
        </h3>
      );
    case 'paragraph':
      return (
        <div key={idx} className="mb-5 text-left">
          <ArticleHtmlChunk html={block.text} />
        </div>
      );
    case 'list':
      return (
        <ul key={idx} className="mb-5 space-y-2 text-left">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
              <span className="min-w-0 flex-1">
                <ArticleHtmlChunk html={item} asPlainParagraph={false} />
              </span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <StepList key={idx}>
          {block.items.map((item, i) => (
            <Step key={i} number={i + 1}>
              <p className="mb-1 font-semibold text-gray-900">{item.title}</p>
              <ArticleHtmlChunk html={item.description} className="leading-relaxed" />
            </Step>
          ))}
        </StepList>
      );
    case 'info':
      return (
        <InfoBox key={idx} title={block.title}>
          <ArticleHtmlChunk html={block.content} />
        </InfoBox>
      );
    case 'tip':
      return (
        <TipBox key={idx} title={block.title}>
          <ArticleHtmlChunk html={block.content} />
        </TipBox>
      );
    case 'warning':
      return (
        <WarningBox key={idx} title={block.title}>
          <ArticleHtmlChunk html={block.content} />
        </WarningBox>
      );
    case 'table': {
      const { displayHeaders, displayRows, viraIdx, resultIdx } =
        prepareSmilieShortcutsTable(block);

      return (
        <div key={idx} className="mb-6 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {displayHeaders.map((h, i) => (
                  <th
                    key={i}
                    className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-700"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {displayRows.map((row, ri) => (
                <tr key={ri} className="transition-colors hover:bg-gray-50">
                  {displayHeaders.map((_, ci) => {
                    if (
                      ci === resultIdx &&
                      resultIdx >= 0 &&
                      viraIdx >= 0 &&
                      displayHeaders[resultIdx]?.toUpperCase() === 'RESULTADO'
                    ) {
                      const codeSrc = (row[viraIdx] ?? '').trim();
                      const inner = smilieInnerFromParensCell(codeSrc);
                      if (inner) {
                        return (
                          <td
                            key={ci}
                            className="px-4 py-3 align-middle text-gray-700"
                          >
                            <img
                              src={smileyStripUrl(inner)}
                              alt={codeSrc}
                              width={36}
                              height={36}
                              className="inline-block h-9 w-9 object-contain align-middle"
                              loading="lazy"
                              decoding="async"
                            />
                          </td>
                        );
                      }
                    }
                    const cell = row[ci] ?? '';
                    return (
                      <td key={ci} className="px-4 py-3 align-top text-gray-700">
                        <ArticleHtmlChunk
                          html={cell}
                          asPlainParagraph={false}
                          className="text-sm"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'divider':
      return <hr key={idx} className="my-8 border-gray-200" />;
    case 'code':
      return (
        <div key={idx} className="mb-6 text-left">
          {block.language?.trim() ? (
            <div className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-gray-500">
              {block.language}
            </div>
          ) : null}
          <pre className="overflow-x-auto rounded-xl border border-gray-800 bg-[#0d1117] p-4 text-sm leading-relaxed text-emerald-100 shadow-inner">
            <code className="block whitespace-pre-wrap font-mono">{block.code}</code>
          </pre>
        </div>
      );
    default:
      return null;
  }
}
